import { cva, type VariantProps } from 'class-variance-authority'
import { Slot } from 'radix-ui'
import {
  Badge as ShadcnBadge,
  badgeVariants as shadcnBadgeVariants,
} from '@/components/ui/badge'
import { cn } from '@/lib/utils'

const colorBadgeVariants = cva('', {
  variants: {
    variant: {
      emerald:
        'bg-emerald-500 text-white focus-visible:ring-emerald-500/20 dark:bg-emerald-700 dark:focus-visible:ring-emerald-500/40 [a&]:hover:bg-emerald-600',
      sky: 'bg-sky-500 text-white focus-visible:ring-sky-500/20 dark:bg-sky-700 dark:focus-visible:ring-sky-500/40 [a&]:hover:bg-sky-600',
      purple:
        'bg-purple-500 text-white focus-visible:ring-purple-500/20 dark:bg-purple-700 dark:focus-visible:ring-purple-500/40 [a&]:hover:bg-purple-600',
      pink: 'bg-pink-500 text-white focus-visible:ring-pink-500/20 dark:bg-pink-700 dark:focus-visible:ring-pink-500/40 [a&]:hover:bg-pink-600',
    },
  },
})

type ColorVariant = NonNullable<
  VariantProps<typeof colorBadgeVariants>['variant']
>
type ShadcnVariant = NonNullable<
  VariantProps<typeof shadcnBadgeVariants>['variant']
>

type Props = Omit<React.ComponentProps<typeof ShadcnBadge>, 'variant'> & {
  variant?: ShadcnVariant | ColorVariant
}

const colorVariants: ReadonlyArray<ColorVariant> = [
  'emerald',
  'sky',
  'purple',
  'pink',
]

const isColorVariant = (variant: unknown): variant is ColorVariant =>
  typeof variant === 'string' && (colorVariants as string[]).includes(variant)

export const Badge = ({
  className,
  variant = 'default',
  asChild = false,
  ...props
}: Props) => {
  if (isColorVariant(variant)) {
    const Comp = asChild ? Slot.Root : 'span'
    return (
      <Comp
        data-slot="badge"
        data-variant={variant}
        className={cn(
          shadcnBadgeVariants(),
          colorBadgeVariants({ variant }),
          className
        )}
        {...props}
      />
    )
  }

  return (
    <ShadcnBadge
      variant={variant}
      asChild={asChild}
      className={className}
      {...props}
    />
  )
}
